import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, subDays } from "date-fns";
import {
    Loader2,
    Plus,
    FileDown,
    FileUp,
    Receipt,
    X,
    FileText
} from "lucide-react";
import { DataTable } from "../components/ui/DataTable";
import SearchableSelectBox from "../components/ui/SearchableSelectBox";
import { fetchVoidedInvoicesForStaff, fetchStoresWhereStaffAssigned } from "../apiHelper";
import { useToast } from "../hooks/useToast";

const VoidedInvoices = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDt, setStartDt] = useState(() => {
        const oneDayAgo = subDays(new Date(), 0);
        return format(oneDayAgo, "yyyy-MM-dd"); // ISO-like for datetime-local
    });

    const [endDt, setEndDt] = useState(() =>
        format(new Date(), "yyyy-MM-dd")
    );

    // State for Floating Action Menu
    const [showActions, setShowActions] = useState(false);

    // Load user
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast({ message: "Please login to view invoices.", show: true, toastType: 0 });
            navigate("/login");
            return;
        }
        try {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
        } catch {
            showToast({ message: "Invalid session.", show: true, toastType: 2 });
            navigate("/login");
        }
    }, [showToast, navigate]);

    useEffect(() => {
        const initializeStoresAndData = async (parsedUser) => {
            try {
                const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
                setStores(fetchedStores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                })));
                if (fetchedStores.length > 0) {
                    const defaultStoreId = fetchedStores[0].store_id;
                    setSelectedStoreId(defaultStoreId);
                }
            } catch (err) {
                console.error(err);
                showToast({ message: err.response?.data?.detail || "Failed to load assigned stores.", show: true, toastType: 2 });
            }
        };

        if (user?.users_id) {
            initializeStoresAndData(user);
        }
    }, [user, showToast]);

    // Fetch invoices
    useEffect(() => {
        if (!user?.users_id || !selectedStoreId) return;

        const loadInvoices = async () => {
            setLoading(true);
            try {
                const payload = {
                    users_id: user.users_id,
                    store_id: selectedStoreId,
                    start_dt: startDt ? startDt : undefined,
                    end_dt: endDt ? endDt : undefined,
                };
                // console.log(payload)
                const data = await fetchVoidedInvoicesForStaff(payload);
                console.log(invoices)
                setInvoices(Array.isArray(data) ? data : []);
            } catch (err) {
                showToast({ message: "Failed to load invoices.", show: true, toastType: 2 });
                setInvoices([]);
            } finally {
                setLoading(false);
            }
        };

        loadInvoices();
    }, [user, selectedStoreId, startDt, endDt, showToast]);

    const formatDateTime = (d) => (d ? format(new Date(d), "d MMM yyyy, hh:mm a") : "—");
    const formatCurrency = (a) => (a == null ? "₦0.00" : new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(a));

    const getDaysStatus = (dueDateStr) => {
        if (!dueDateStr) return null;

        const dueDate = new Date(dueDateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);

        const diffTime = dueDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return { text: "Due today", color: "bg-orange-100 text-orange-800" };
        if (diffDays > 0) return { text: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}`, color: "bg-blue-100 text-blue-800" };
        return { text: `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} past due`, color: "bg-red-100 text-red-800" };
    };

    const getStatus = (cs) => {
        if (!cs) return { label: "Unknown", color: "bg-gray-100 text-gray-700" };
        if (cs.is_paid_in_full) return { label: "Paid", color: "bg-green-100 text-green-800" };
        if (cs.payment_due_date) {
            const daysInfo = getDaysStatus(cs.payment_due_date);
            if (daysInfo) return { label: daysInfo.text, color: daysInfo.color };
        }
        return { label: "Pending", color: "bg-yellow-100 text-yellow-800" };
    };

    const calculateBalanceDue = (creditsale) => {
        if (!creditsale) return 0;
        const totalPaid = creditsale.payments?.reduce((sum, payment) => sum + (Number(payment.payment_amount) || 0), 0) || 0;
        return Math.max(0, creditsale.sale_amount - totalPaid);
    };

    const columns = [
        { key: "placed_on", label: "Date", render: (v) => formatDateTime(v) },
        { key: "idempotency_key", label: "Invoice #", render: (_, r) => r.idempotency_key || `INV-${r.orders_id}` },
        {
            key: "customer",
            label: "Customer",
            render: (_, r) => {
                const u = r.customer?.user;
                return [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim() || "Unknown Customer";
            },
        },
        {
            key: "creditsale",
            label: "Status",
            render: (_, r) => {
                const { label, color } = getStatus(r.creditsale);
                return <span className={`px-3 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
            },
        },
        {
            key: "payment_due_date",
            label: "Due Date",
            render: (_, r) => (r.creditsale?.payment_due_date ? format(new Date(r.creditsale.payment_due_date), "d MMM yyyy") : "—"),
        },
        {
            key: "sale_amount",
            label: "Amount",
            render: (_, r) => formatCurrency(r.creditsale?.sale_amount ?? 0)
        },
        {
            key: "balance_due",
            label: "Balance Due",
            render: (_, r) => {
                const balance = calculateBalanceDue(r.creditsale);
                const isOverdue = r.creditsale?.payment_due_date && new Date(r.creditsale.payment_due_date) < new Date();
                return (
                    <span className={`font-semibold ${balance > 0 ? (isOverdue ? 'text-red-600' : 'text-orange-600') : 'text-green-600'}`}>
                        {formatCurrency(balance)}
                    </span>
                );
            }
        },
    ];

    const handleRowAction = (actionKey, row) => {
        switch (actionKey) {
            case "view":
                const allInvoicesRich = invoices.map(inv => {
                    const { label: statusLabel, color: statusColor } = getStatus(inv.creditsale);
                    const currencySymbol = inv.order_currency?.setcnote || "₦";
                    const balanceDue = calculateBalanceDue(inv.creditsale);
                    return {
                        orders_id: inv.orders_id,
                        creditsale_id: inv.creditsale.creditsale_id,
                        idempotency_key: inv.idempotency_key || `INV-${inv.orders_id}`,
                        placed_on: inv.placed_on,
                        status: { label: statusLabel, color: statusColor, isPaid: inv.creditsale?.is_paid_in_full || false, isOverdue: statusLabel.includes("past due") },
                        due_date: inv.creditsale?.payment_due_date || null,
                        balance_due: balanceDue,
                        balance_due_formatted: `${currencySymbol}${balanceDue.toFixed(2)}`,
                        total_amount: inv.creditsale?.sale_amount || 0,
                        currency_symbol: currencySymbol,
                        currency_code: inv.order_currency?.setccurr || "NGN",
                        customer: {
                            name: [inv.customer?.user?.first_name, inv.customer?.user?.last_name].filter(Boolean).join(" ") || "Unknown Customer",
                            member_id: inv.customer?.member_id
                        },
                        store: { name: inv.store?.identifier || "Unknown Store", store_id: inv.store_id }
                    };
                });
                const selectedInvoice = allInvoicesRich.find(i => i.orders_id === row.orders_id);
                navigate(`/sales/invoices/view`, { state: { selectedInvoice, allInvoices: allInvoicesRich } });
                break;

            case "receive_payment":
                navigate('/sales/invoices/receive-payment', {
                    state: {
                        creditsale_id: row.creditsale.creditsale_id,
                        orders_id: row.creditsale.orders_id,
                        customer_id: row.customer.member_id,
                        invoice_number: row.idempotency_key,
                        store_id: row.store_id,
                        currency: row.order_currency.setccurr
                    },
                });
                break;
        }
    };

    const totals = invoices.reduce((acc, inv) => {
        const saleAmount = Number(inv.creditsale?.sale_amount || 0);
        const balance = calculateBalanceDue(inv.creditsale);

        acc.totalSales += saleAmount;
        acc.totalOutstanding += balance;
        if (balance > 0) acc.countOutstanding += 1;

        return acc;
    }, { totalSales: 0, totalOutstanding: 0, countOutstanding: 0 });

    const rowActions = [
        { key: "view", label: "View Details" },
        { key: "receive_payment", label: "Receive Payment" },
    ];

    const quickActions = [
        { label: "New Invoice", icon: Plus, onClick: () => navigate("/sales/invoices/new") },
        { label: "New Credit Note", icon: Receipt, onClick: () => navigate("/sales/credit-notes/new") },
        { label: "Import Invoices", icon: FileUp, onClick: () => console.log("Import") },
        { label: "Export Invoices", icon: FileDown, onClick: () => console.log("Export") },
    ];

    if (!user) return null;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full mx-auto pt-6 px-4 sm:px-6">
                {/* Header */}
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="text-gmail-blue" />
                        Credit Invoices
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Manage credit sales, monitor aging balances, and track payment status
                    </p>
                </div>

                <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <SearchableSelectBox
                                name="store_id"
                                label="Store"
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                options={stores}
                                placeholder="Select store..."
                                required={true}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={startDt}
                                onChange={(e) => setStartDt(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gmail-blue focus:border-gmail-blue"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={endDt}
                                onChange={(e) => setEndDt(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gmail-blue focus:border-gmail-blue"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-500 font-medium">Total Credit Value</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalSales)}</p>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-orange-500">
                        <p className="text-sm text-gray-500 font-medium">Total Balance Due</p>
                        <p className="text-2xl font-bold text-orange-600">{formatCurrency(totals.totalOutstanding)}</p>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-500 font-medium">Unpaid Invoices</p>
                        <p className="text-2xl font-bold text-gray-900">{totals.countOutstanding} <span className="text-sm font-normal text-gray-400">items</span></p>
                    </div>
                </div>

                {/* Full-Width Content */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-32">
                            <Loader2 className="animate-spin h-12 w-12 text-gmail-blue" />
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={invoices}
                            title=""
                            rowActions={rowActions}
                            onAction={handleRowAction}
                            classNames="border-none"
                        />
                    )}
                </div>
            </div>

            {/* --- FLOATING ACTION BUTTON & TOOLTIP MENU --- */}
            <div className="fixed bottom-8 right-8 z-50 flex flex-col items-end">
                {/* Action Menu Items */}
                {showActions && (
                    <div className="mb-4 flex flex-col gap-4 items-end animate-in fade-in slide-in-from-bottom-4 duration-200">
                        {quickActions.map((action, idx) => (
                            <div key={idx} className="flex items-center gap-3 group">
                                <span className="bg-gray-800 text-white text-xs px-3 py-1.5 rounded shadow-lg font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                    {action.label}
                                </span>
                                <button
                                    onClick={() => {
                                        action.onClick();
                                        setShowActions(false);
                                    }}
                                    className="p-4 bg-white text-gmail-blue rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-100 active:scale-95"
                                >
                                    <action.icon size={20} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Primary Toggle FAB */}
                <button
                    onClick={() => setShowActions(!showActions)}
                    className={`flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300 transform active:scale-90 ${showActions ? "bg-gray-800 rotate-45" : "bg-gmail-blue hover:bg-blue-600"
                        } text-white`}
                >
                    {showActions ? <X size={32} /> : <Plus size={32} />}
                </button>

                {/* Overlay Backdrop */}
                {showActions && (
                    <div
                        className="fixed inset-0 -z-10 bg-black/10 backdrop-blur-[2px] transition-all"
                        onClick={() => setShowActions(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default VoidedInvoices;